import React from 'react';
import ReactDOM from 'react-dom/client';
import {App} from 'antd';
import './index.css';
import './styles/tailwind.css'; // 引入 Tailwind CSS
import WjApp from './App';
import * as Component from './components';
import reportWebVitals from './reportWebVitals';
// import { BrowserRouter } from 'react-router-dom';
import './index.css';
import { SafeStorage, detectWebView, sendLogToApp } from './utils/jsbridge';

// 运行时判断是否为调试模式（通过URL参数）
const urlParams = new URLSearchParams(window.location.search);
const isDebugMode = urlParams.has('debug') || urlParams.has('DEBUG') || (window.location.hostname.startsWith('192.'));

// 调试面板组件
const DebugPanel = () => {
  const [logs, setLogs] = React.useState([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const panelRef = React.useRef(null);

  // 从SafeStorage加载日志
  React.useEffect(() => {
    SafeStorage.getItem('debugPanelLogs').then((savedLogs) => {
      if (savedLogs) {
        try {
          setLogs(JSON.parse(savedLogs));
        } catch (e) {
          console.error('Failed to parse logs from SafeStorage:', e);
        }
      }
    }).catch((e) => {
      console.error('Failed to load logs from SafeStorage:', e);
    });
  }, []);

  // 添加日志
  const addLog = (log) => {
    const timestamp = new Date().toLocaleTimeString();
    const formattedLog = `[${timestamp}] ${log}`;
    
    setLogs(prevLogs => {
      const newLogs = [...prevLogs, formattedLog].slice(-50); // 只保留最近100条日志
      // 保存到SafeStorage
      SafeStorage.setItem('debugPanelLogs', JSON.stringify(newLogs)).catch((e) => {
        console.error('Failed to save logs to SafeStorage:', e);
      });
      return newLogs;
    });
  };

  // 清空日志
  const clearLogs = () => {
    setLogs([]);
    SafeStorage.removeItem('debugPanelLogs').catch((e) => {
      console.error('Failed to clear logs from SafeStorage:', e);
    });
  };

  // 将addLog函数暴露到全局作用域，方便其他地方调用
  React.useEffect(() => {
    window.debugPanel = {
      addLog
    };

    return () => {
      delete window.debugPanel;
    };
  }, []);

  // 点击面板外部关闭面板
  React.useEffect(() => {
    // 添加消息监听器
    window.document.addEventListener('message', handleReactNativeMessages);
    window.addEventListener('message', handleReactNativeMessages);
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 使用现有的WebView检测函数
  const { isWebView } = detectWebView();
  
  // 仅在调试模式下渲染调试面板
  if (!isDebugMode) {
    return null;
  }

  return (
    <div style={{ position: 'fixed', top: '20px', left: '20px', zIndex: 9999 }}>
      {/* 调试按钮 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '10px',
          backgroundColor: '#ff6b6b',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '14px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
        }}
      >
        调试
      </button>

      {/* 调试面板 */}
      {isOpen && (
        <div
          ref={panelRef}
          style={{
            position: 'absolute',
            top: '50px',
            left: '20px',
            width: '300px',
            maxHeight: '400px',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '5px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            zIndex: 10000,
            overflow: 'hidden'
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
            backgroundColor: '#f0f0f0',
            borderBottom: '1px solid #ccc'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>调试面板</h3>
            <button
              onClick={clearLogs}
              style={{
                padding: '5px 10px',
                backgroundColor: '#ff6b6b',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              清空
            </button>
          </div>
          <div style={{ padding: '10px', maxHeight: '350px', overflowY: 'auto' }}>
            {logs.length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center' }}>暂无日志</p>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    padding: '5px 0',
                    borderBottom: '1px solid #eee',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}
                >
                  {log}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// 劫持console方法 - 仅在调试模式或WebView中启用
const { isWebView } = detectWebView();
if (isDebugMode || isWebView) {
  // 保存原始console方法
  const originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info
  };

  // 重写console方法
  console.log = function(...args) {
    // 调用原始console.log
    originalConsole.log.apply(console, args);
    
    // 添加到调试面板
    if (window.debugPanel && window.debugPanel.addLog) {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, null, 2);
        }
        return String(arg);
      }).join(' ');
      window.debugPanel.addLog(`[LOG] ${message}`);
    }
    
    // 发送到React Native
    if (isWebView) {
      sendLogToApp(`[LOG] ${args.join(' ')}`);
    }
  };

  console.warn = function(...args) {
    // 调用原始console.warn
    originalConsole.warn.apply(console, args);
    
    // 添加到调试面板
    if (window.debugPanel && window.debugPanel.addLog) {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, null, 2);
        }
        return String(arg);
      }).join(' ');
      window.debugPanel.addLog(`[WARN] ${message}`);
    }
    
    // 发送到React Native
    if (isWebView) {
      sendLogToApp(`[WARN] ${args.join(' ')}`);
    }
  };

  console.error = function(...args) {
    // 调用原始console.error
    originalConsole.error.apply(console, args);
    
    // 添加到调试面板
    if (window.debugPanel && window.debugPanel.addLog) {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, null, 2);
        }
        return String(arg);
      }).join(' ');
      window.debugPanel.addLog(`[ERROR] ${message}`);
    }
    
    // 发送到React Native
    if (isWebView) {
      sendLogToApp(`[ERROR] ${args.join(' ')}`);
    }
  };

  console.info = function(...args) {
    // 调用原始console.info
    originalConsole.info.apply(console, args);
    
    // 添加到调试面板
    if (window.debugPanel && window.debugPanel.addLog) {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, null, 2);
        }
        return String(arg);
      }).join(' ');
      window.debugPanel.addLog(`[INFO] ${message}`);
    }
    
    // 发送到React Native
    if (isWebView) {
      sendLogToApp(`[INFO] ${args.join(' ')}`);
    }
  };
}

// 监听来自React Native的消息
const handleReactNativeMessages = (event) => {
  try {
    const data = JSON.parse(event.data);
    
    // 处理推送令牌准备就绪的消息
    if (data.type === 'PUSH_TOKEN_READY') {
      // 将推送令牌存储到全局变量或SafeStorage中
      if (data.payload && data.payload.expoPushToken) {
        const { expoPushToken, deviceId, os, deviceModel, deviceBrand } = data.payload;
        SafeStorage.setItem('expoPushToken', expoPushToken);
        SafeStorage.setItem('deviceId', deviceId);
        SafeStorage.setItem('os', os);
        SafeStorage.setItem('deviceModel', deviceModel);
        SafeStorage.setItem('deviceBrand', deviceBrand);
      }
    }

    if (data.type === 'USER_LOGIN_RESPONSE') {
      // 处理用户登录响应的消息
      console.log('Webview received user login response:', data.payload);
      if (data.payload && data.payload.userId) {
        const { userId, expoPushToken, deviceId, os, deviceModel, deviceBrand } = data.payload;
        
        // 把推送令牌请求接口存储起来
        fetch('/api/app/push/save-token', {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            expoPushToken,
            deviceId,
            os,
            deviceModel,
            deviceBrand,
          })
        }).then(response => {
          if (response.ok) {
            console.log('Push token saved successfully');
          } else {
            console.error('Failed to save push token');
          }
        }).catch(error => {
          console.error('Error saving push token:', error);
        });
      }
    }
    // 可以在这里处理其他类型的消息
  } catch (e) {
    console.warn('解析来自React Native的消息失败:', e);
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
const rootEle = document.getElementById('root');
const componentTag = rootEle.getAttribute('entry');
const Com = Component[componentTag];

const generateClientID = () => {
  // 在这里使用适合您的唯一ID生成算法，比如UUID
  // 这里简单地使用时间戳加上随机数作为示例
  return Math.random().toString(36).substring(2, 11) + Date.now();
}

// if(!Cookie.get('SHclientID')){
//   Cookie.set('SHclientID', generateClientID(), 365);
// }
// console.log(window.location.pathname);
const pathname = window.location.pathname;
// console.log(pathname.substring(0,2));

root.render(
  <App>
    <WjApp>{componentTag && Com ? <Com /> : <h1>Oops! 404</h1>}</WjApp>
    <DebugPanel />
  </App>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();